// RolePeek — the role peek body, rendered by the global PeekHost from
// `?peek=roles:<id>` (ADR-036 §2), on THE platform peek grammar (P uplift).
// Fetches the role DETAIL (the list item carries no grants) and mirrors the
// detail page's boxed sections dense — Details (description) + Capabilities
// wrapping the read-only CapabilitySummary (the SAME shared component the detail +
// create-review render, so they can't drift) — and the ACTS composed by the
// panel from the role's ONE registry (`useRoleActions`, ADR-036 §7): the top
// row = Edit capabilities (the primary, a quiet button — the focused editor's
// route) + Edit role (the one edit, a plain button), the footer = the labelled
// Actions door (Clone · Disable / Reactivate) + "Open full page". The overlays
// the acts open are mounted ONCE, beside the panel. The registry gates every
// act on can('admin').

import { useTranslation } from 'react-i18next';
import type { RoleDetail } from '@ems-app/schemas/roles/responses';
import { useRole, useRoles } from '../../hooks/useRoles.js';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { Alert } from '../../components/ui/Alert.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { icons } from '../../components/ui/icons.js';
import { CapabilitySummary } from '../../components/patterns/capabilities/CapabilitySummary.js';
import { RoleDormantNarrowings, roleNarrowingSummary } from '../../components/patterns/capabilities/roleNarrowings.js';
import { useRoleActions } from './RoleActions.js';
import { RoleStatusBadge, lockLabel } from './roleDisplay.js';

export interface RolePeekProps {
  projectId: string;
  roleId: number;
  onClose: () => void;
}

export function RolePeek({ projectId, roleId, onClose }: RolePeekProps) {
  const { t } = useTranslation('roles');
  const role = useRole(projectId, roleId);

  if (role.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('list.title')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (role.isError || !role.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('detail.error')}>
        <Alert variant="danger">{t('detail.error')}</Alert>
      </ContextPanel>
    );
  }
  return <RolePeekBody projectId={projectId} role={role.data} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a role that is there.
function RolePeekBody({ projectId, role: r, onClose }: { projectId: string; role: RoleDetail; onClose: () => void }) {
  const { t } = useTranslation('roles');
  const allRoles = useRoles(projectId);
  const { acts, overlays } = useRoleActions(projectId, r);
  const availableRoles = (allRoles.data ?? []).map((x) => ({ id: x.id, name: x.name }));
  const subtitle = `${t('peek.usersCount', { count: r.user_count })} · ${lockLabel(t, r.inactivity_timeout_min)}`;

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.role size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
        title={r.name}
        kind={subtitle}
        status={<RoleStatusBadge disabled={r.is_disabled} />}
        openFullPath={`/p/${projectId}/roles/${r.id}`}
        acts={acts}
      >
        {r.description && (
          <DetailSection dense title={t('detail.sections.details')}>
            <p className="text-sm text-[var(--color-text-secondary)]">{r.description}</p>
          </DetailSection>
        )}
        <DetailSection dense title={t('detail.sections.capabilities')}>
          <CapabilitySummary
            grants={r.permissions}
            narrowingSummary={roleNarrowingSummary(t, { managedRoles: r.managed_roles, availableRoles, responsibilityLevels: r.responsibility_levels })}
            emptyMessage={t('summary.empty')}
          />
          <RoleDormantNarrowings grants={r.permissions} managedRoles={r.managed_roles} availableRoles={availableRoles} responsibilityLevels={r.responsibility_levels} />
        </DetailSection>
      </ContextPanel>
      {overlays}
    </>
  );
}
